import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, CardHeader, CardContent, Skeleton } from '@pagespeedify/ui';
import { ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import { Task } from '../types';
import { User } from '../lib/auth/types';
import TaskGitHubStatus from './TaskGitHubStatus';

type TabType = 'ai-tasks' | 'reports' | 'monitoring';

interface Report {
    id: string;
    // URL variations from API
    website_url?: string;
    page_url?: string;
    url?: string;
    // Date variations
    created_at?: string;
    generatedAt?: string;
    // Score variations
    performanceScore?: number;
    performance_score?: number;
    // Other fields
    ai_solutions_count?: number;
    createdById?: string | null;
    created_by_id?: string | null;
    createdByEmail?: string | null;
    created_by_email?: string | null;
}

interface MonitoredUrl {
    id: string;
    website_url: string;
    is_active: boolean;
    status?: string;
    priority?: string;
    device_type?: string;
    additional_metrics?: {
        breach_state?: Record<string, { breached?: boolean }>;
    };
}

interface InsightsTabsCardProps {
    // AI Tasks data
    tasks: Task[];
    isLoadingTasks: boolean;
    aiTaskStatusFilter: string;
    setAiTaskStatusFilter: (val: string) => void;
    aiTaskPriorityFilter: string;
    setAiTaskPriorityFilter: (val: string) => void;
    aiTasksPage: number;
    setAiTasksPage: (fn: (p: number) => number) => void;

    // Reports data
    reports: Report[];
    isLoadingReports: boolean;
    reportsStatusFilter: string;
    setReportsStatusFilter: (val: string) => void;
    reportsPriorityFilter: string;
    setReportsPriorityFilter: (val: string) => void;
    reportsPage: number;
    setReportsPage: (fn: (p: number) => number) => void;

    // Monitoring data
    monitoringUrls: MonitoredUrl[];
    isLoadingMonitoring: boolean;
    monitoringStatusFilter: string;
    setMonitoringStatusFilter: (val: string) => void;
    monitoringPriorityFilter: string;
    setMonitoringPriorityFilter: (val: string) => void;
    monitoringPage: number;
    setMonitoringPage: (fn: (p: number) => number) => void;

    // User info for creator badges
    user?: User | null;
}

const PAGE_SIZE = 5;

export default function InsightsTabsCard({
    tasks,
    isLoadingTasks,
    aiTaskStatusFilter,
    setAiTaskStatusFilter,
    aiTaskPriorityFilter,
    setAiTaskPriorityFilter,
    aiTasksPage,
    setAiTasksPage,
    reports,
    isLoadingReports,
    reportsStatusFilter,
    setReportsStatusFilter,
    reportsPriorityFilter,
    setReportsPriorityFilter,
    reportsPage,
    setReportsPage,
    monitoringUrls,
    isLoadingMonitoring,
    monitoringStatusFilter,
    setMonitoringStatusFilter,
    monitoringPriorityFilter,
    setMonitoringPriorityFilter,
    monitoringPage,
    setMonitoringPage,
    user,
}: InsightsTabsCardProps) {
    const [activeTab, setActiveTab] = React.useState<TabType>('ai-tasks');

    // Filter AI Tasks
    const filteredTasks = React.useMemo(() => {
        let filtered = tasks.filter(t => t.has_ai_history || t.github_issue_id || t.github_pr_id || t.github_commit_sha);
        if (aiTaskStatusFilter !== 'all') {
            filtered = filtered.filter(t => t.status === aiTaskStatusFilter);
        }
        if (aiTaskPriorityFilter !== 'all') {
            filtered = filtered.filter(t => {
                const p = typeof t.priority === 'number' ?
                    (t.priority > 2 ? 'high' : t.priority > 1 ? 'medium' : 'low') :
                    (t.priority?.toString().toLowerCase() || 'medium');
                return p === aiTaskPriorityFilter;
            });
        }
        return filtered.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    }, [tasks, aiTaskStatusFilter, aiTaskPriorityFilter]);

    const paginatedTasks = filteredTasks.slice(aiTasksPage * PAGE_SIZE, (aiTasksPage + 1) * PAGE_SIZE);

    // Filter Reports
    const filteredReports = React.useMemo(() => {
        let filtered = reports;
        if (reportsStatusFilter !== 'all') {
            filtered = filtered.filter(r => {
                const score = r.performanceScore ?? r.performance_score ?? 0;
                if (reportsStatusFilter === 'good') return score >= 90;
                if (reportsStatusFilter === 'needs_improvement') return score >= 50 && score < 90;
                if (reportsStatusFilter === 'poor') return score < 50;
                return true;
            });
        }
        return filtered;
    }, [reports, reportsStatusFilter]);

    const paginatedReports = filteredReports.slice(reportsPage * PAGE_SIZE, (reportsPage + 1) * PAGE_SIZE);

    // Filter Monitoring
    const filteredMonitoring = React.useMemo(() => {
        let filtered = monitoringUrls.filter(u => u.is_active);
        if (monitoringStatusFilter !== 'all') {
            filtered = filtered.filter(u => {
                const breachState = u.additional_metrics?.breach_state || {};
                const breachCount = Object.values(breachState).filter(s => s?.breached).length;
                if (monitoringStatusFilter === 'good') return breachCount === 0;
                if (monitoringStatusFilter === 'needs_improvement') return breachCount > 0 && breachCount < 3;
                if (monitoringStatusFilter === 'poor') return breachCount >= 3;
                return true;
            });
        }
        return filtered;
    }, [monitoringUrls, monitoringStatusFilter]);

    const paginatedMonitoring = filteredMonitoring.slice(monitoringPage * PAGE_SIZE, (monitoringPage + 1) * PAGE_SIZE);

    const getScoreColor = (score: number): string => {
        if (score >= 90) return 'text-report-good';
        if (score >= 50) return 'text-report-improvement';
        return 'text-report-fail';
    };

    const getCurrentPage = () => {
        if (activeTab === 'ai-tasks') return aiTasksPage;
        if (activeTab === 'reports') return reportsPage;
        return monitoringPage;
    };

    const getTotalItems = () => {
        if (activeTab === 'ai-tasks') return filteredTasks.length;
        if (activeTab === 'reports') return filteredReports.length;
        return filteredMonitoring.length;
    };

    const handlePrevPage = () => {
        if (activeTab === 'ai-tasks') setAiTasksPage(p => Math.max(0, p - 1));
        else if (activeTab === 'reports') setReportsPage(p => Math.max(0, p - 1));
        else setMonitoringPage(p => Math.max(0, p - 1));
    };

    const handleNextPage = () => {
        if (activeTab === 'ai-tasks') setAiTasksPage(p => p + 1);
        else if (activeTab === 'reports') setReportsPage(p => p + 1);
        else setMonitoringPage(p => p + 1);
    };

    const tabItems = [
        { id: 'ai-tasks' as TabType, label: 'AI Tasks', count: filteredTasks.length },
        { id: 'reports' as TabType, label: 'Reports', count: filteredReports.length },
        { id: 'monitoring' as TabType, label: 'Monitoring', count: filteredMonitoring.length },
    ];

    const navigate = useNavigate();

    return (
        <Card className="w-full overflow-hidden">
            {/* Tab Header with Pagination */}
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                        {tabItems.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id
                                    ? 'bg-primary text-primary-foreground'
                                    : 'hover:bg-muted text-muted-foreground'
                                    }`}
                            >
                                {tab.label} ({tab.count})
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handlePrevPage}
                            disabled={getCurrentPage() === 0}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={handleNextPage}
                            disabled={(getCurrentPage() + 1) * PAGE_SIZE >= getTotalItems()}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>

            {/* Filters Row */}
            <div className="px-6 pb-3 flex items-center justify-end gap-2">
                {activeTab === 'ai-tasks' && (
                    <>
                        <select
                            value={aiTaskStatusFilter}
                            onChange={(e) => { setAiTaskStatusFilter(e.target.value); setAiTasksPage(() => 0); }}
                            className="h-8 px-2 text-xs rounded-md border border-input bg-background w-auto"
                        >
                            <option value="all">All Status</option>
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                        </select>
                        <select
                            value={aiTaskPriorityFilter}
                            onChange={(e) => { setAiTaskPriorityFilter(e.target.value); setAiTasksPage(() => 0); }}
                            className="h-8 px-2 text-xs rounded-md border border-input bg-background w-auto"
                        >
                            <option value="all">All Priority</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                    </>
                )}
                {activeTab === 'reports' && (
                    <>
                        <select
                            value={reportsStatusFilter}
                            onChange={(e) => { setReportsStatusFilter(e.target.value); setReportsPage(() => 0); }}
                            className="h-8 px-2 text-xs rounded-md border border-input bg-background w-auto"
                        >
                            <option value="all">All Status</option>
                            <option value="good">Good</option>
                            <option value="needs_improvement">Needs Improvement</option>
                            <option value="poor">Poor</option>
                        </select>
                        <select
                            value={reportsPriorityFilter}
                            onChange={(e) => { setReportsPriorityFilter(e.target.value); setReportsPage(() => 0); }}
                            className="h-8 px-2 text-xs rounded-md border border-input bg-background w-auto"
                        >
                            <option value="all">All Priority</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                    </>
                )}
                {activeTab === 'monitoring' && (
                    <>
                        <select
                            value={monitoringStatusFilter}
                            onChange={(e) => { setMonitoringStatusFilter(e.target.value); setMonitoringPage(() => 0); }}
                            className="h-8 px-2 text-xs rounded-md border border-input bg-background w-auto"
                        >
                            <option value="all">All Status</option>
                            <option value="good">Good</option>
                            <option value="needs_improvement">Needs Improvement</option>
                            <option value="poor">Poor</option>
                        </select>
                        <select
                            value={monitoringPriorityFilter}
                            onChange={(e) => { setMonitoringPriorityFilter(e.target.value); setMonitoringPage(() => 0); }}
                            className="h-8 px-2 text-xs rounded-md border border-input bg-background w-auto"
                        >
                            <option value="all">All Priority</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                    </>
                )}
            </div>

            {/* Content */}
            <CardContent className="min-h-[300px] overflow-hidden">
                {/* AI Tasks Tab */}
                {activeTab === 'ai-tasks' && (
                    <div className="space-y-4 overflow-hidden max-w-full">
                        {isLoadingTasks ? (
                            <>
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                            </>
                        ) : paginatedTasks.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No AI-powered tasks found. Tasks with AI solutions or GitHub integrations will appear here.
                            </div>
                        ) : (
                            paginatedTasks.map((task: Task) => (
                                <div
                                    key={task.id}
                                    onClick={() => {
                                        if (task.type === 'agent') {
                                            navigate(`/agent?taskId=${task.id}`);
                                        } else if (task.type === 'monitoring') {
                                            navigate(`/monitoring`, { state: { selectedTask: task } });
                                        } else if (task.type === 'image_optimization') {
                                            navigate(`/image-optimization`, { state: { taskId: task.id } });
                                        } else {
                                            navigate(`/tasks/${task.id}`);
                                        }
                                    }}
                                    className="block hover:bg-muted/50 p-3 rounded-lg cursor-pointer"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Zap className="h-5 w-5 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">{task.title}</div>
                                            <div className="text-sm text-muted-foreground truncate">{task.description?.substring(0, 100)}</div>
                                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                                <TaskGitHubStatus task={task} user={user || null} />
                                                {user?.internalUserId === (task as any).creator_id && (
                                                    <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">Owner</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Reports Tab */}
                {activeTab === 'reports' && (
                    <div className="space-y-4 overflow-hidden max-w-full">
                        {isLoadingReports ? (
                            <>
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                            </>
                        ) : paginatedReports.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No reports found. Generate your first performance audit to get started.
                            </div>
                        ) : (
                            paginatedReports.map((report) => {
                                const score = report.performanceScore ?? report.performance_score ?? 0;
                                const websiteUrl = report.website_url || report.page_url || report.url || 'Unknown URL';
                                const createdAt = report.created_at || report.generatedAt;
                                const createdBy = report.createdByEmail || report.created_by_email || null;
                                let formattedDate = 'Unknown';
                                if (createdAt) {
                                    try {
                                        const dateObj = new Date(createdAt);
                                        if (!isNaN(dateObj.getTime())) {
                                            formattedDate = dateObj.toLocaleDateString();
                                        }
                                    } catch {
                                        formattedDate = 'Invalid Date';
                                    }
                                }

                                let ratingClasses = 'bg-report-fail-light text-report-fail';
                                let ratingLabel = 'Poor';
                                if (score >= 90) {
                                    ratingClasses = 'bg-report-good-light text-report-good';
                                    ratingLabel = 'Good';
                                } else if (score >= 50) {
                                    ratingClasses = 'bg-report-improvement-light text-report-improvement';
                                    ratingLabel = 'Needs Improvement';
                                }

                                return (
                                    <Link key={report.id} to={`/reports/${report.id}`} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 overflow-hidden">
                                        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                                            <span className="font-semibold truncate block">{websiteUrl}</span>
                                            <span className="text-sm text-muted-foreground truncate">
                                                {formattedDate}{createdBy ? ` by ${createdBy}` : ''}
                                            </span>
                                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                                                <div className={`text-xs px-2 py-1 rounded-full ${ratingClasses}`}>{ratingLabel}</div>
                                                {(report.ai_solutions_count ?? 0) > 0 && (
                                                    <div className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">AI Solution Available</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className={`text-3xl font-bold flex-shrink-0 ml-4 ${getScoreColor(score)}`}>{score}</div>
                                    </Link>
                                );
                            })
                        )}
                    </div>
                )}

                {/* Monitoring Tab */}
                {activeTab === 'monitoring' && (
                    <div className="space-y-4 overflow-hidden max-w-full">
                        {isLoadingMonitoring ? (
                            <>
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                                <Skeleton className="h-16 w-full" />
                            </>
                        ) : paginatedMonitoring.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No monitored URLs. <Link to="/monitoring" className="text-primary hover:underline">Add a URL</Link> to start monitoring.
                            </div>
                        ) : (
                            paginatedMonitoring.map((url) => {
                                const breachState = url.additional_metrics?.breach_state || {};
                                const breachCount = Object.values(breachState).filter(s => s?.breached).length;
                                let ratingClasses = 'bg-report-good-light text-report-good';
                                let ratingLabel = 'Good';
                                if (breachCount >= 3) {
                                    ratingClasses = 'bg-report-fail-light text-report-fail';
                                    ratingLabel = 'Poor';
                                } else if (breachCount > 0) {
                                    ratingClasses = 'bg-report-improvement-light text-report-improvement';
                                    ratingLabel = 'Needs Improvement';
                                }

                                return (
                                    <Link key={url.id} to={`/monitoring?selected=${url.id}`} className="block p-3 rounded-lg hover:bg-muted/50 overflow-hidden">
                                        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                                            <span className="font-semibold truncate block">{url.website_url}</span>
                                            <span className="text-sm text-muted-foreground">
                                                {breachCount > 0 ? `${breachCount} threshold${breachCount > 1 ? 's' : ''} breached` : 'All thresholds passed'}
                                            </span>
                                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                                                <div className={`text-xs px-2 py-1 rounded-full ${ratingClasses}`}>{ratingLabel}</div>
                                                {url.status && (
                                                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 capitalize">{url.status}</span>
                                                )}
                                                {url.priority && (
                                                    <span className={`text-xs px-2 py-1 rounded-full capitalize ${url.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                                                        url.priority === 'medium' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                                                            'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                                        }`}>{url.priority}</span>
                                                )}
                                                {url.device_type && (
                                                    <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 capitalize">{url.device_type}</span>
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })
                        )}
                    </div>
                )}
            </CardContent>

            {/* Footer with View All link */}
            <div className="p-4 border-t border-border">
                {activeTab === 'ai-tasks' && (
                    <Link to="/tasks" className="text-sm text-primary hover:text-primary/80 font-medium">
                        View All Tasks →
                    </Link>
                )}
                {activeTab === 'reports' && (
                    <Link to="/reports" className="text-sm text-primary hover:text-primary/80 font-medium">
                        View All Reports →
                    </Link>
                )}
                {activeTab === 'monitoring' && (
                    <Link to="/monitoring" className="text-sm text-primary hover:text-primary/80 font-medium">
                        View All Monitoring ({filteredMonitoring.length} URLs) →
                    </Link>
                )}
            </div>
        </Card>
    );
}
